import { ProductListAnalyticsService, WishListAnalyticsService } from '@analytics/feature';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { PAGE_SIZE } from '@catalog/const/v2';
import { CityService } from '@city/feature';
import { CustomProduct } from '@product/const';
import { ProductListReviewsService } from '@reviews/data-access';
import { WishlistEntry } from '@wishlist/const';
import { WishlistService } from '@wishlist/feature';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'sd-wishlist-content-entries',
  templateUrl: './wishlist-content-entries.component.html',
  styleUrls: ['./wishlist-content-entries.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    WishListAnalyticsService,
    {
      provide: ProductListAnalyticsService,
      useExisting: WishListAnalyticsService,
    },
  ],
  standalone: false,
})
export class WishlistContentEntriesComponent implements OnInit {
  private readonly wishlistService = inject(WishlistService);
  private readonly cityService = inject(CityService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly productListReviewsService = inject(ProductListReviewsService);
  private readonly wishListAnalyticsService = inject(WishListAnalyticsService);
  private readonly pageSize = PAGE_SIZE;

  entries = input<WishlistEntry[]>([]);

  protected searchWishlist = toSignal(this.wishlistService.getWishlist(), {
    initialValue: { results: [], pagination: { currentPage: 0, totalPages: 0 } },
  });

  ngOnInit(): void {
    combineLatest({
      params: this.activatedRoute.queryParams,
      city: this.cityService.currentCity$,
    })
      .pipe(
        map(({ params, city }) => {
          const page = Number(params['page']) || 0;

          return {
            page,
            baseStoreId: city?.baseStoreId,
          };
        }),
        distinctUntilChanged((prev, curr) => prev.page === curr.page && prev.baseStoreId === curr.baseStoreId),
        switchMap(({ page }) => {
          return new Observable<{ products: CustomProduct[]; page: number }>((observer) => {
            this.wishlistService.loadPage(page, this.entries(), (products, pageNum) => {
              observer.next({ products, page: pageNum });
              observer.complete();
            });
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ products, page }) => {
        this.onProductsLoaded(products, page);
      });
  }

  private onProductsLoaded(products: CustomProduct[], page: number): void {
    const startIndex = page * this.pageSize;
    const entriesToLoad = this.entries().slice(startIndex, startIndex + this.pageSize);

    this.productListReviewsService.loadReviews(entriesToLoad.map((entry) => entry.productCode));
    this.wishListAnalyticsService.onViewProductList(products, page + 1);
  }

  trackByEntry(_index: number, product: CustomProduct): string {
    return product.code;
  }
}
